import React, { useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { Grid } from '@mui/material';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementProps } from 'models/form';
import { StyledTextField } from '../FormInput/styles';

interface IFormCode extends IFormElementProps {
  disabled?: boolean;
  required?: boolean;
  setValue?: any;
  withLabel?: boolean;
  countNumber?: number;
  startFocus?: boolean;
  onSubmit?: () => void;
}

export const FormCode = ({
  name,
  label,
  control,
  errors,
  clearErrors,
  setValue,
  disabled,
  required,
  withLabel = true,
  withValidation = true,
  countNumber = 6,
  startFocus = false,
  onSubmit,
}: IFormCode) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const prevValueRef = useRef<string[]>([]);

  const handleChange = (index: number, e: any) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 1) return;
    setValue(`${name}[${index}]`, value);
    prevValueRef.current[index] = value;
    if (errors[name]?.message) {
      clearErrors(name);
    } else {
      clearErrors(`${name}[${index}]`);
    }

    if (value && index < countNumber - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }

    if (onSubmit) {
      const list = inputRefs.current.filter((input) => input?.value);
      if (list.length === countNumber) {
        onSubmit();
      }
    }
  };

  useEffect(() => {
    if (inputRefs.current[0] && startFocus) {
      inputRefs.current[0]?.focus();
    }
  }, []);

  const handleKeyDown = (key: number, event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace') {
      if (!prevValueRef.current[key] && key > 0 && inputRefs.current[key - 1]) {
        inputRefs.current[key - 1]?.focus();
      }
    }
  };

  return (
    <StyledFormElementWrapper>
      <Grid sx={{ maxWidth: "400px", m: "auto" }}>
        {withLabel && (
          <FormLabel name={name} label={label} disabled={disabled} required={required} />
        )}
        <Grid sx={{ gap: 1, justifyContent: "space-between", flexWrap: "nowrap", mb: "3px", mx: "auto" }}>
          {[...Array(countNumber)].map((_, index) => (
            <Controller
              name={`${name}[${index}]`}
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  code
                  // eslint-disable-next-line no-return-assign
                  inputRef={(el: any) => (inputRefs.current[index] = el)}
                  variant="outlined"
                  size="small"
                  disabled={disabled}
                  slotProps={{
                    htmlInput: {
                      maxLength: 1, style: { textAlign: 'center' }
                    },
                  }}
                  onChange={(e: any) => handleChange(index, e)}
                  onKeyDown={(event: any) => handleKeyDown(index, event)}
                  error={(errors[name] && errors[name][index]) || errors[name]?.message}
                />
              )}
            />
          ))}
        </Grid>
        {withValidation && <FormErrorLabel label={errors[name]?.message} />}
      </Grid>
    </StyledFormElementWrapper>
  );
};
